import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'
import { SentryCrashButton } from './components/SentryCrashButton.tsx'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

import './styles.css'
import reportWebVitals from './reportWebVitals.ts'
import { initializeAuth } from './lib/auth-initializer.ts'

let router: ReturnType<typeof createRouter>

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment:
      import.meta.env.VITE_SENTRY_ENV ??
      import.meta.env.MODE ??
      process.env.NODE_ENV ??
      'development',
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1,
    ),
    integrations: [Sentry.browserTracingIntegration()],
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

async function bootstrap() {
  await initializeAuth()

  const TanStackQueryProviderContext = TanStackQueryProvider.getContext()
  router = createRouter({
    routeTree,
    context: {
      ...TanStackQueryProviderContext,
    },
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultStructuralSharing: true,
    defaultPreloadStaleTime: 0,
  })

  const rootElement = document.getElementById('app')
  if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement)
    root.render(
      <StrictMode>
        <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
          <RouterProvider router={router} />
          <SentryCrashButton />
        </TanStackQueryProvider.Provider>
      </StrictMode>,
    )
  }

  reportWebVitals()
}

bootstrap()
